import { Command, Option } from "clipanion";
import { DefaultAzureCredential, ManagedIdentityCredential, type TokenCredential } from "@azure/identity";
import { applyRequestToEntity } from "@m8t-stack/platform-release";
import { M8tCommand } from "../../lib/m8t-command.js";
import { LocalCliError } from "../../lib/errors.js";
import { openApplyTable, readApplyRequest } from "../../lib/apply-request-store.js";
import { decideClear, clearedRow } from "../../lib/apply-intent-clear.js";
import { renderJson, resolveOutputMode } from "../../lib/output.js";

export class PlatformClearIntentCommand extends M8tCommand {
  static paths = [["platform", "clear-intent"]];
  static usage = Command.Usage({
    description: "Clear a stuck platform update request so a new one can be made.",
    details:
      "An update request that never settles blocks every later request, and the routines that would normally rescue it all run inside the updater itself — which is no help when the updater is what is stuck. This clears it from outside. Refuses while an update may still be running, unless --force.",
    examples: [
      ["Clear a stuck request", "m8t platform clear-intent --resource-group rg-m8t --subscription <id>"],
      ["Clear one whose lease is still live", "m8t platform clear-intent --resource-group rg-m8t --subscription <id> --force"],
    ],
  });

  subscription = Option.String("--subscription");
  resourceGroup = Option.String("--resource-group");
  miClientId = Option.String("--mi-client-id", { description: "Authenticate as this user-assigned managed identity instead of the ambient credential." });
  force = Option.Boolean("--force", false, {
    description: "Clear even while the lease is live. Only when you know the engine that held it is gone.",
  });
  output = Option.String("--output");

  async executeCommand(): Promise<number> {
    const mode = resolveOutputMode(this.output as "pretty" | "json" | "auto" | undefined, this.context.stdout as NodeJS.WriteStream);
    const need = (v: unknown, flag: string): string => {
      if (typeof v !== "string" || v.trim().length === 0) {
        throw new LocalCliError({ code: "PLATFORM_CLEAR_INTENT_ARG_MISSING", message: `${flag} is required.` });
      }
      return v.trim();
    };

    const credential: TokenCredential =
      typeof this.miClientId === "string" && this.miClientId.trim().length > 0
        ? new ManagedIdentityCredential({ clientId: this.miClientId.trim() })
        : new DefaultAzureCredential();

    const client = await openApplyTable({
      credential,
      subscriptionId: need(this.subscription, "--subscription"),
      resourceGroup: need(this.resourceGroup, "--resource-group"),
    });

    const row = await readApplyRequest(client);
    const decision = decideClear(row, new Date().toISOString(), this.force);

    // A refusal is a non-zero exit, not a message on a successful run: this is
    // most often reached from a script or a runbook step, where "printed a
    // warning and exited 0" reads as done.
    if (decision.action === "refuse") {
      throw new LocalCliError({
        code: "PLATFORM_CLEAR_INTENT_REFUSED",
        message: decision.reason,
        hint: "Re-run with --force once you have confirmed no converge is running.",
      });
    }

    if (decision.action === "none" || !row) {
      this.context.stdout.write(
        mode === "json" ? renderJson({ cleared: false, reason: decision.reason }) + "\n" : `${decision.reason}\n`,
      );
      return 0;
    }

    const next = clearedRow(row, new Date().toISOString());
    // Unconditional write, no ETag: the row this clears is by definition one that
    // nothing else is advancing. An ETag race here would mean an engine picked it
    // up between the read and the write — which is precisely the outcome that
    // makes clearing unnecessary, and losing that race is harmless.
    await client.updateEntity(applyRequestToEntity(next) as never, "Merge");

    const result = { cleared: true, previousStatus: row.status, target: row.target, reason: decision.reason };
    this.context.stdout.write(
      mode === "json"
        ? renderJson(result) + "\n"
        : `cleared: ${row.status} → failed (target ${row.target}). A new update can now be requested.\n`,
    );
    return 0;
  }
}
